/**
 * Cast-concrete divider for the farrowing bays.
 *
 * The upper arris is deliberately a real, small chamfer instead of a contrasting
 * strip. At workbench scale a painted stripe reads as decoration, while the
 * catch-light from a physical arris is enough to keep the otherwise severe wall
 * legible in the long, repetitive hall view.
 */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

function buildKerbGeometry(): THREE.BufferGeometry {
  const profile: ReadonlyArray<readonly [number, number]> = [
    [-0.06, 0], [0.06, 0], [0.06, 0.52], [0.045, 0.55], [-0.045, 0.55], [-0.06, 0.52],
  ];
  const positions: number[] = [];
  const indices: number[] = [];
  for (const z of [-1.2, 1.2]) for (const [x, y] of profile) positions.push(x, y, z);
  const count = profile.length;
  for (let i = 0; i < count; i++) {
    const next = (i + 1) % count;
    indices.push(i, next, count + i, next, count + next, count + i);
  }
  for (let i = 1; i < count - 1; i++) {
    indices.push(0, i + 1, i, count, count + i, count + i + 1);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'pen-kerb';
  const concrete = surface('mineral', 0x77807d);
  const kerb = new THREE.Mesh(buildKerbGeometry(), concrete);
  kerb.name = 'cast-kerb';
  kerb.castShadow = true;
  kerb.receiveShadow = true;
  group.add(kerb);

  // Formwork joins repeat at bay centres. Keeping the seam shallow prevents it
  // becoming fake weathering while still breaking the silhouette in grazing light.
  const seam = new THREE.Mesh(new THREE.BoxGeometry(0.122, 0.012, 0.012), surface('mineral', 0x606966));
  seam.name = 'formwork-seam';
  seam.position.set(0, 0.275, 0);
  group.add(seam);
  return group;
}

export const penKerbAsset: AssetDefinition = {
  id: 'pen-kerb', label: 'Pen kerb', description: 'Cast concrete bay divider with a chamfered arris and formwork joint.',
  footprint: { width: 0.12, height: 0.55, length: 2.4 }, create: build,
};
